import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { appRoutes } from 'config';
import { marketing } from 'config/global';

const STEP_KEYS = ['save', 'comeBack', 'fillUp'];

const ClockIcon = () => (
  <svg viewBox="0 0 16 16" className={styles.clock} aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" />
    <path d="M8 4.5V8l2.5 2" strokeLinecap="round" />
  </svg>
);

const DeviceIcon = () => (
  <svg viewBox="0 0 24 24" className={styles.deviceGlyph} aria-hidden="true">
    <rect x="3" y="4" width="13" height="10" rx="1.5" />
    <rect x="16" y="7" width="5" height="13" rx="1.5" />
    <path d="M3 14h13M7 18h5" />
  </svg>
);

export const Section34 = () => {
  const t = useTranslations();
  const r = (key: string) =>
    t(`pages.useCases.memoryKeeping.reassurance.${key}`);
  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.head}>
          <a href="#how" className={styles.howLink}>
            <span className={styles.playIcon}>
              <svg
                viewBox="0 0 24 24"
                className={styles.playGlyph}
                aria-hidden="true"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
            {r('howLink')}
          </a>
          <h2 className={styles.title}>{r('title')}</h2>
          <p className={styles.lead}>{r('lead')}</p>
        </div>

        <div className={styles.grid}>
          {STEP_KEYS.map((key, i) => (
            <div key={key} className={styles.card}>
              <div className={styles.num}>{i + 1}</div>
              <h3 className={styles.cardTitle}>{r(`steps.${key}.title`)}</h3>
              <p className={styles.cardBody}>{r(`steps.${key}.body`)}</p>
              <div className={styles.time}>
                <ClockIcon />
                {r(`steps.${key}.time`)}
              </div>
            </div>
          ))}

          {/* Step 4 — CTA card */}
          <div className={styles.ctaCard}>
            <div className={styles.ctaNum}>4</div>
            <h3 className={styles.cardTitle}>{r('ctaTitle')}</h3>
            <div className={styles.ctaButtons}>
              <a href={appRoutes.LOGIN} className={styles.ctaPrimary}>
                {r('ctaStart')}
              </a>
              <a
                href={marketing.appStoreUri}
                target="_blank"
                rel="noreferrer noopener"
                className={styles.ctaOutline}
              >
                <DeviceIcon /> {t('pages.useCases.memoryKeeping.storeIos')}
              </a>
              <a
                href={marketing.googlePlayUri}
                target="_blank"
                rel="noreferrer noopener"
                className={styles.ctaOutline}
              >
                <DeviceIcon /> {t('pages.useCases.memoryKeeping.storeAndroid')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy pt-[74px] pb-5',
  wrap: 'mx-auto max-w-[1200px] px-6 lg:px-10',
  head: 'mx-auto max-w-[680px] text-center',
  howLink: classNames(
    'mb-5 inline-flex items-center gap-2 text-base font-semibold transition-colors',
    'text-mf-blue hover:text-mf-off-black-600',
    'marketing-dark:text-mf-coral marketing-dark:hover:text-white',
  ),
  playIcon: classNames(
    'flex h-8 w-8 items-center justify-center rounded-full',
    'bg-[#EBF0F7] text-mf-blue',
    'marketing-dark:bg-mf-coral/20 marketing-dark:text-mf-coral',
  ),
  playGlyph: 'h-3.5 w-3.5 fill-current',
  title:
    'font-inter text-3xl font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[40px]',
  lead: 'mx-auto mt-6 max-w-[600px] text-lg leading-[1.6] text-mf-gray-600 marketing-dark:text-white/66',
  grid: 'mt-12 grid grid-cols-1 gap-[22px] text-left sm:grid-cols-2 lg:grid-cols-4',
  card: classNames(
    'flex flex-col rounded-[18px] border p-7',
    'border-[#F0F0F0] bg-white',
    'marketing-dark:border-white/10 marketing-dark:bg-white/[0.03]',
  ),
  num: classNames(
    'flex h-[42px] w-[42px] items-center justify-center rounded-[12px] text-[17px] font-extrabold',
    'bg-[#EBF0F7] text-mf-blue',
    'marketing-dark:bg-mf-coral/15 marketing-dark:text-mf-coral',
  ),
  cardTitle:
    'mb-3 mt-5 font-inter text-xl font-bold text-mf-off-black-600 marketing-dark:text-white',
  cardBody:
    'mb-auto text-sm leading-[1.65] text-mf-gray-600 marketing-dark:text-white/60',
  time: classNames(
    'mt-5 flex items-center gap-2 border-t pt-4 text-sm font-medium',
    'border-[#F0F0F0] text-mf-gray-500',
    'marketing-dark:border-white/10 marketing-dark:text-white/55',
  ),
  clock: 'h-3.5 w-3.5 fill-none stroke-current stroke-[1.5]',
  ctaCard: classNames(
    'flex flex-col rounded-[18px] border p-7',
    'border-mf-blue/30 bg-[#EBF0F7]',
    'marketing-dark:border-mf-blue/40 marketing-dark:bg-mf-blue/15',
  ),
  ctaNum: classNames(
    'flex h-[42px] w-[42px] items-center justify-center rounded-[12px] text-[17px] font-extrabold',
    'bg-mf-blue text-white marketing-dark:bg-white/12',
  ),
  ctaButtons: 'mt-auto flex flex-col gap-3',
  ctaPrimary:
    'flex items-center justify-center rounded-full bg-mf-blue px-5 py-3 text-sm font-bold text-white transition-transform hover:-translate-y-0.5',
  ctaOutline: classNames(
    'flex items-center justify-center gap-2 rounded-full border px-5 py-3 text-sm font-bold transition-colors',
    'border-mf-gray-300 bg-white text-mf-off-black-600 hover:border-mf-gray-500',
    'marketing-dark:border-white/25 marketing-dark:bg-transparent marketing-dark:text-white marketing-dark:hover:border-white/60',
  ),
  deviceGlyph: 'h-3.5 w-3.5 fill-none stroke-current stroke-2',
};
